import React from 'react';
import { Role } from 'sections/user-management/sections/roles/data/roles-constants';
import { useRoles } from 'sections/user-management/data/RolesApi';
import Spinner from 'core/ui/components/spinner';

export interface IRolesContext {
  roles: Role[];
  loading: boolean;
  isError: boolean;
  error: Error;
  getRole: (roleId: string) => Role;
  addNewRole: (role: Role) => void;
  updateLocalRole: (roleId: string, role: Role) => void;
}

const RolesContext = React.createContext<IRolesContext | undefined>(undefined);

export const RolesProvider: React.FC = ({ children }) => {
  const { rolesData: roles, isRolesLoading: loading, isRolesError: isError, rolesError: error } = useRoles();
  const getRole = (roleId: string): Role => roles.filter(role => role.id === roleId)[0];
  const updateLocalRole = (roleId: string, role: Role) => {
    roles.splice(
      roles.findIndex(role => role.id === roleId),
      1,
      role,
    );
  };
  const addNewRole = (role: Role) => roles.push(role);

  if (loading) {
    return <Spinner />;
  }

  return (
    <RolesContext.Provider
      value={{
        roles,
        loading,
        isError,
        error,
        getRole,
        updateLocalRole,
        addNewRole,
      }}
    >
      {children}
    </RolesContext.Provider>
  );
};

export const useRolesContext = (): IRolesContext => {
  const context = React.useContext(RolesContext);

  if (!context) {
    throw new Error('useRolesContext should only be called within a RolesContext.Provider');
  }

  return context;
};
